// sentabr-preview — w16 "Sentabr" card-preview.
//
// A regal autumn-wedding invitation. Outer frame is a gold-foil
// gradient, inside a midnight card with a gold hairline, a circular
// photo medallion ringed by gold + a Cinzel "S & N" monogram, and the
// names stacked across an ampersand in the template's Cinzel face.
// Mirrors the rose-gold structure verbatim so the catalog reads as
// one family of paper cards.
import { Photo, type LayoutCtx } from "./_shared";

export function SentabrPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  const FOIL = `linear-gradient(135deg, #6E5224 0%, ${accent} 22%, #E8C880 45%, ${accent} 65%, #6E5224 100%)`;
  const [a, b] = names.split(/\s*&\s*/);
  const initials =
    ((a?.[0] ?? "S") + " & " + (b?.[0] ?? "N")).toUpperCase();

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.5em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TO'Y MAROSIMI
        </div>

        {/* Round photo medallion with gold ring + monogram */}
        <div
          style={{
            width: "62%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: FOIL,
              padding: "5%",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                padding: "4%",
                boxSizing: "border-box",
                boxShadow: `inset 0 0 0 1px ${accent}66`,
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Photo
                  src={photo}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  fallback={
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: `radial-gradient(circle at 50% 45%, ${accent}55, ${bg} 70%)`,
                        display: "grid",
                        placeItems: "center",
                        fontFamily: font,
                        fontSize: 28,
                        color: accent,
                        letterSpacing: "0.08em",
                      }}
                    >
                      {initials}
                    </div>
                  }
                />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: `linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.35) 100%)`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Names — Cinzel, uppercase, stacked across "&". */}
        <div
          style={{
            fontFamily: font,
            fontSize: 22,
            fontWeight: 500,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: ink,
            lineHeight: 1.1,
          }}
        >
          {a ?? names}
        </div>
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 16,
            color: accent,
            margin: "2px 0",
          }}
        >
          &amp;
        </div>
        <div
          style={{
            fontFamily: font,
            fontSize: 22,
            fontWeight: 500,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: ink,
            lineHeight: 1.1,
          }}
        >
          {b ?? ""}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TO'Y MAROSIMI · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
